<template>
  <div class="external-user-table">
    <v-data-table
      class="elevation-1"
      item-key="id"
      :headers="tableHeaders"
      :items="filteredUsers"
      :loading="tableLoading"
      :single-select="true"
      :search="tableSearch"
      :custom-filter="tableFilter"
      :group-by="tableGroupBy"
      show-group-by
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [5, 10, 25, 50]
      }"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-text-field
            v-model="tableSearch"
            prepend-icon="mdi-magnify"
            class="mr-4"
            clearable
            hide-details
            single-line
            dense
          ></v-text-field>
          <v-divider
            class="mx-4"
            inset
            vertical
          ></v-divider>
          <add-external-user-button
            :allowedUserRoles="['LgSpotBauPMOAss']"
            :text="''"
            :icon="icons.mdiAccountPlusOutline"
            :realm="realm"
            :companies="computedCompanies"
            @userAdded="loadTable"
          />
          <v-btn
            color="primary"
            :loading="tableLoading || submitting"
            dark
            icon
            @click="loadTable"
          >
            <v-icon>mdi-cached</v-icon>
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:[`item.companyId`]="{ item }">
        {{ getCompanyName(item.companyId) }}
      </template>
      <template v-slot:[`item.disabledAt`]="{ item }">
        <v-checkbox
          :input-value="item.disabledAt !== null"
          readonly
          hide-details
          class="shrink mr-2 mt-0"
        />
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ item.createdAt !== null ? formatDate(item.createdAt) : '' }}
      </template>
      <template v-slot:[`item.lastLoggedIn`]="{ item }">
        {{ item.createdAt !== null ? formatDate(item.lastLoggedIn) : '' }}
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              class="mr-2"
              v-bind="attrs"
              v-on="on"
              @click="editItem(item)"
            >
              mdi-pencil
            </v-icon>
          </template>
          <span>{{ item.username }} bearbeiten</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :color="item.disabledAt !== null ? 'green' : 'red'"
              small
              class="mr-2"
              v-bind="attrs"
              v-on="on"
              @click="blockItem(item)"
            >
              mdi-cancel
            </v-icon>
          </template>
          <span>{{ item.username }} {{ item.disabledAt !== null ? 'entsperren' : 'sperren' }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="orange"
              small
              class="mr-2"
              v-bind="attrs"
              v-on="on"
              @click="setNewPasswordDialog(item)"
            >
              {{ icons.mdiLockReset }}
            </v-icon>
          </template>
          <span>Neues Password setzen</span>
        </v-tooltip>
      </template>
      <template v-slot:[`group.header`]="state">
        <td
          :colspan="state.headers.length - 1"
          class="text-start"
        >
          <v-btn
            icon
            @click="state.toggle"
          >
            <v-icon :style="{ transform: state.isOpen ? 'rotate(180deg)' : 'unset' }">
              {{ icons.mdiChevronUp }}
            </v-icon>
          </v-btn>
          {{ tableHeaders.find(h => h.value === state.groupBy[0]).text }}:
          <template v-if="state.groupBy[0] === 'companyId'">
            {{ getCompanyName(state.group) }}
          </template>
          <template v-else>
            {{ state.group }}
          </template>
          ({{ state.items.length }})
        </td>
        <td class="text-end">
          <v-btn
            icon
            @click="state.remove"
          >
            <v-icon>
              {{ icons.mdiWindowClose }}
            </v-icon>
          </v-btn>
        </td>
      </template>
      <template v-slot:[`footer.prepend`]>
        <v-switch
          v-model="hideDisabledUser"
          label="Gesperrte Benutzer ausblenden"
          class="pa-3"
        ></v-switch>
      </template>
    </v-data-table>
    <v-dialog
      v-model="dialogEdit"
      width="max(30vw, 500px)"
      persistent
    >
      <v-card v-if="editedItem">
        <v-card-title>
          Benutzer&nbsp;<b>{{ editedItem.username }}</b>&nbsp;bearbeiten
        </v-card-title>
        <v-card-subtitle>{{ getCompany(editedItem) }}</v-card-subtitle>
        <v-card-text>
          <v-form
            ref="editForm"
            v-model="editFormValid"
            :disabled="submitting"
          >
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  md="5"
                >
                  <v-text-field
                    label="Benutzername (readonly)"
                    readonly
                    required
                    :value="editedItem.username"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="7"
                >
                  <v-text-field
                    v-model="editedItem.mail"
                    label="E-Mail (readonly)"
                    :rules="emailRules"
                    required
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="editedItem.name"
                    label="Name"
                    :rules="nameRules"
                    required
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="editedItem.lastName"
                    label="Nachname"
                    :rules="nameRules"
                    required
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-autocomplete
                    v-if="(realm || editedItem.companyId) && computedCompanies"
                    ref="companySelector"
                    v-model="editedItem.companyId"
                    :rules="companyRules"
                    :items="computedCompanies"
                    item-text="name"
                    item-value="id"
                    label="Firma"
                    required
                    @change="editedItem.company = computedCompanies.find(f => f.id === $event).name"
                  ></v-autocomplete>
                  <v-text-field
                    v-else
                    ref="companyName"
                    v-model="editedItem.company"
                    :rules="companyRules"
                    label="Firma"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-combobox
                    v-model="editedItem.userGroups"
                    :label="'Gruppen' + (realm !== null ? ' (readonly)' : '')"
                    :items="validUserGroups"
                    :readonly="realm !== null"
                    multiple
                    small-chips
                  >
                    <template
                      v-if="fullAdminWhitelist"
                      slot="append-outer"
                    >
                      <v-btn
                        color="accent"
                        icon
                        @click="addGroup"
                      >
                        <v-icon dark>
                          mdi-plus
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-combobox>
                </v-col>
                <v-col cols="6">
                  <v-checkbox
                    v-model="editedItem.passwordResetRequired"
                    :disabled="!fullAdminWhitelist"
                    label="Password Reset benötigt"
                    required
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            :loading="submitting"
            :disabled="!isEditUserChanged"
            depressed
            text
            @click="editUser(editedItem)"
          >
            Speichern
          </v-btn>
          <v-btn
            color="primary"
            depressed
            text
            @click="dialogEdit = !dialogEdit"
          >
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogBlock"
      width="max(20vw, 350px)"
    >
      <v-card v-if="editedItem">
        <v-card-text class="pt-6">
          <p>
            Soll der Benutzer <code>{{ editedItem.username }}</code> {{ editedItem.disabledAt !== null ? 'entsperrt' : 'gesperrt' }} werden?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            :loading="submitting"
            depressed
            text
            @click="blockUser"
          >
            Ja, {{ editedItem.disabledAt !== null ? 'entsperren' : 'sperren' }}
          </v-btn>
          <v-btn
            color="primary"
            depressed
            text
            @click="dialogBlock = !dialogBlock"
          >
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogSetNewPassword"
      width="max(20vw, 400px)"
    >
      <v-card v-if="editedItem">
        <v-card-title>
          Neues Password für&nbsp;<b>{{ editedItem.username }}</b>
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form
            ref="newPasswordForm"
            v-model="newPasswordFormValid"
            :disabled="submitting"
          >
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="newPassword.password"
                    :rules="passwordRules"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    label="Neues Passwort"
                    counter
                    required
                    @click:append="showPassword = !showPassword"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="newPassword.confirmPassword"
                    :rules="[newPassword.password === newPassword.confirmPassword || 'Die Passwörter stimmen nicht überein']"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    label="Neues Passwort bestätigen"
                    counter
                    required
                    @click:append="showPassword = !showPassword"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            :loading="submitting"
            :disabled="!newPasswordFormValid"
            depressed
            text
            @click="setNewPassword"
          >
            Password ändern
          </v-btn>
          <v-btn
            color="primary"
            depressed
            text
            @click="dialogSetNewPassword = !dialogSetNewPassword"
          >
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import _ from 'lodash';
import {
  mdiAccountPlusOutline,
  mdiChevronUp,
  mdiWindowClose,
  mdiLockReset
} from '@mdi/js';

import { HTTP } from '@/main/httpClient.js';
import TimeUtility from '@/util/TimeUtility.js';
import ExternalUser from '@/main/ExternalUser.js';

import { showNotification } from '@/main/NotificationObject.js';
import AddExternalUserButton from './AddExternalUserButton.vue';

export default {
  name: 'ExternalUserTable',
  components: {
    AddExternalUserButton
  },
  props: {
    companies: {
      type: Array,
      required: false,
      default: null
    },
    realm: {
      type: Number,
      required: false,
      default: null
    }
  },
  data: () => ({
    icons: {
      mdiAccountPlusOutline,
      mdiChevronUp,
      mdiWindowClose,
      mdiLockReset
    },
    tableLoading: true,
    tableSearch: '',
    allTableHeaders: [
      {
        text: 'Username',
        align: 'start',
        sortable: true,
        value: 'username',
        groupable: false
      },
      {
        text: 'Type',
        align: 'start',
        sortable: true,
        value: 'realm',
        groupable: true
      },
      {
        text: 'Vorname',
        align: 'start',
        sortable: false,
        value: 'name',
        groupable: false
      },
      {
        text: 'Nachname',
        align: 'start',
        sortable: false,
        value: 'lastName',
        groupable: false
      },
      {
        text: 'E-Mail',
        align: 'start',
        sortable: false,
        value: 'mail',
        groupable: false
      },
      {
        text: 'Firma',
        align: 'start',
        sortable: true,
        value: 'company',
        groupable: true
      },
      {
        text: 'Firma',
        align: 'start',
        sortable: true,
        value: 'companyId',
        groupable: true
      },
      {
        text: 'Gruppen',
        align: 'start',
        sortable: false,
        value: 'userGroups',
        groupable: false
      },
      {
        text: 'Gesperrt',
        align: 'start',
        sortable: true,
        value: 'disabledAt',
        groupable: false
      },
      {
        text: 'Angelegt',
        align: 'start',
        sortable: true,
        value: 'createdAt',
        groupable: false
      },
      {
        text: 'Letzter Login',
        align: 'start',
        sortable: true,
        value: 'lastLoggedIn',
        groupable: false
      },
      {
        text: 'Aktionen',
        align: 'start',
        sortable: false,
        value: 'actions',
        groupable: false,
        width: '105px'
      }
    ],
    users: [],
    hideDisabledUser: true,
    submitting: false,
    editedIndex: -1,
    editedItem: null,
    dialogAdd: true,
    dialogEdit: false,
    dialogBlock: false,
    dialogSetNewPassword: false,
    editFormValid: null,
    newPasswordFormValid: null,
    newPassword: {
      password: '',
      confirmPassword: ''
    },
    validUserGroups: [
      {
        text: 'LgSpotNU',
        disabled: true
      }
    ],
    nameRules: [
      (v) => !!v || 'Name ist ein Pflichtfeld',
      (v) => v.length >= 3 || 'Name ist ein Pflichtfeld'
    ],
    companyRules: [
      (v) => !!v || 'Firma ist ein Pflichtfeld',
      (v) =>
        v.length >= 5 || 'Der Firmenname muss mindestens 5 Zeichen lang sein'
    ],
    emailRules: [
      (v) => !!v || 'E-Mailist ein Pflichtfeld',
      (v) => /.+@.+\..{2,}/.test(v) || 'E-Mail ist nicht valide'
    ],
    passwordRules: ExternalUser.PASSWORD_RULES,
    showPassword: false,
    fetchedCompanies: null
  }),
  computed: {
    username() {
      return this.$store.getters.getUsername;
    },
    fullAdminWhitelist() {
      const usernameWhitelist = ['jweber', 'marndt', 'mwohlrab'];
      return usernameWhitelist.includes(this.username);
    },
    filteredUsers() {
      return this.users.filter(
        (u) => !this.hideDisabledUser || u.disabledAt === null
      );
    },
    tableHeaders() {
      const tableHeaders = _.cloneDeep(this.allTableHeaders);

      if (this.companies === null) {
        const companyIdIndex = tableHeaders.findIndex(
          (h) => h.value === 'companyId'
        );
        tableHeaders.splice(companyIdIndex, 1);
      } else {
        const companyIndex = tableHeaders.findIndex(
          (h) => h.value === 'company'
        );
        tableHeaders.splice(companyIndex, 1);
      }

      if (this.realm !== null) {
        const realmIndex = tableHeaders.findIndex((h) => h.value === 'realm');
        tableHeaders.splice(realmIndex, 1);
      }

      return tableHeaders;
    },
    tableGroupBy() {
      if (this.companies !== null && this.realm !== null) {
        return 'companyId';
      }

      if (this.realm === null) {
        return 'realm';
      }

      return null;
    },
    isEditUserChanged() {
      const originalItem = this.users.find((u) => u.id === this.editedItem.id);
      for (const field of Object.keys(this.editedItem)) {
        if (Array.isArray(originalItem[field])) {
          if (!this.setsEqual(this.editedItem[field], originalItem[field])) {
            return true;
          }
        } else if (this.editedItem[field] !== originalItem[field]) {
          return true;
        }
      }
      return false;
    },
    computedCompanies() {
      return this.companies !== null ? this.companies : this.fetchedCompanies;
    }
  },
  created: async function () {
    if (this.companies === null) {
      this.fetchedCompanies = _.orderBy(
        await this.fetchCompanies(),
        ['name'],
        ['asc']
      );
    }
    this.loadTable();
  },
  methods: {
    addGroup: function () {
      const addGroup = prompt('Gruppe hinzufügen', 'LgSpot');
      if (this.validUserGroups.includes(addGroup)) return;
      this.validUserGroups.push(addGroup);
    },
    lowercaseUsername: function () {
      this.newUserDto.username = this.newUserDto.username.toLowerCase();
    },
    loadTable: function () {
      this.tableLoading = true;
      return ExternalUser.get(this.realm)
        .then(({ data }) => {
          this.users = data;

          if (this.companies !== null) {
            // update the company name for users
            this.users.map((u) => {
              if (u.companyId !== null) {
                u.company = this.companies.find(
                  (c) => c.id === u.companyId
                )?.name;
              }
              return u;
            });
          }

          // sort by username
          this.users.sort((a, b) => {
            if (a.username < b.username) return -1;
            if (a.username > b.username) return 1;
            return 0;
          });
        })
        .catch((err) => {
          showNotification([
            'Fehler beim abrufen der Benutzerliste',
            err.message
          ]);
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    editItem: function (item) {
      this.editedIndex = this.users.indexOf(item);
      this.editedItem = _.cloneDeep(item);
      this.dialogEdit = true;
    },
    blockItem: function (item) {
      this.editedIndex = this.users.indexOf(item);
      this.editedItem = _.cloneDeep(item);
      this.dialogBlock = true;
    },
    setNewPasswordDialog: function (item) {
      this.editedIndex = this.users.indexOf(item);
      this.editedItem = _.cloneDeep(item);
      this.dialogSetNewPassword = true;
    },
    setNewPassword: function () {
      this.submitting = true;
      if (
        !this.newPasswordFormValid ||
        this.newPassword.password !== this.newPassword.confirmPassword
      ) {
        return;
      }

      ExternalUser.setNewPassword(this.editedItem.id, this.newPassword.password)
        .catch(() => {
          showNotification('Fehler beim ändern des Passwortes', 'error');
        })
        .finally(async () => {
          await this.loadTable();
          this.newPassword = { password: '', confirmPassword: '' };
          this.submitting = false;
          this.dialogSetNewPassword = false;
        });
    },
    blockUser: function () {
      this.submitting = true;

      ExternalUser.toggleUserBlock(this.editedItem)
        .then(() => {})
        .catch(() => {
          showNotification('Fehler beim bearbeiten des Users', 'error');
        })
        .finally(async () => {
          await this.loadTable();
          this.submitting = false;
          this.dialogBlock = false;
        });
    },
    editUser: function (updateItem) {
      if (!this.editFormValid) return;
      this.submitting = true;
      const modifyFields = [
        'name',
        'lastName',
        'mail',
        'company',
        'companyId',
        'userGroups',
        'passwordResetRequired'
      ];
      const modifyDto = {};
      const originalItem = this.users.find((u) => u.id === updateItem.id);
      for (const field of modifyFields) {
        if (updateItem[field] !== originalItem[field]) {
          if (
            !Array.isArray(updateItem[field]) ||
            updateItem[field].length !== originalItem[field].length ||
            updateItem[field].join() !== originalItem[field].join()
          ) {
            modifyDto[field] = updateItem[field];
          }
        }
      }
      if (Object.keys(modifyDto).length === 0) {
        this.submitting = false;
        return;
      }
      modifyDto.id = updateItem.id;

      const path = `external-users/${this.editedItem.id}`;
      HTTP.patch(path, modifyDto)
        .then(() => {})
        .catch(() => {
          showNotification('Fehler beim bearbeiten des Users', 'error');
        })
        .finally(async () => {
          await this.loadTable();
          this.submitting = false;
          this.dialogEdit = false;
        });
    },
    tableFilter: function (value, search) {
      return (
        value != null &&
        search != null &&
        typeof value === 'string' &&
        value.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
      );
    },
    fetchCompanies() {
      return HTTP.get('/past/companies')
        .then(({ status, data }) => {
          if (status !== 200) {
            showNotification('Fehler beim laden der Firmen', 'warning');
          } else return data;
        })
        .catch(() => {
          showNotification('Fehler beim laden der Firmen', 'error');
        });
    },
    getCompany(item) {
      if (item.companyId && this.computedCompanies) {
        return this.getCompanyName(item.companyId);
      }
      return item.company;
    },
    getCompanyName(companyId) {
      if (!companyId) return '-';
      const company = this.computedCompanies.find((c) => c.id === companyId);
      return company?.name ?? '-';
    },
    setsEqual(a, b) {
      return a.length === b.length && a.every((item) => b.includes(item));
    },
    formatDate(dateString) {
      if (!dateString) return dateString;
      return TimeUtility.formatDateTimeWithoutUhr(dateString);
    }
  }
};
</script>
<style scoped>
.external-user-table {
  overflow-x: auto;
}
</style>
